import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Bell, Mail, MessageSquare, Phone, Shield, AlertTriangle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useLanguage } from "@/contexts/LanguageContext";

export const NotificationPreferences = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  
  const form = useForm({
    defaultValues: {
      emailAlerts: true,
      pushNotifications: false,
      smsAlerts: false,
      scheduleChanges: true,
      incidentReports: true,
      systemAnnouncements: true,
      teamUpdates: true,
      serviceAccounts: true,
    }
  });

  const onSubmit = async (values: any) => {
    setSaving(true);
    // Simulate saving
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    toast({
      title: t('preferencesUpdated'),
      description: t('notificationPreferencesSaved'),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notifications')}</CardTitle>
        <CardDescription>
          {t('manageNotificationPreferences')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="channels">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="channels">{t('notificationChannels')}</TabsTrigger>
            <TabsTrigger value="types">{t('notificationTypes')}</TabsTrigger>
          </TabsList>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <TabsContent value="channels" className="space-y-4">
                <div className="grid gap-4">
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('emailNotifications')}</p>
                        <p className="text-sm text-muted-foreground">{t('receiveNotificationsViaEmail')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="emailAlerts"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bell className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('pushNotifications')}</p>
                        <p className="text-sm text-muted-foreground">{t('receivePushNotificationsOnDevice')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="pushNotifications"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('smsAlerts')}</p>
                        <p className="text-sm text-muted-foreground">{t('receiveCriticalAlertsViaSms')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="smsAlerts"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="types" className="space-y-4">
                <div className="grid gap-4">
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('scheduleChanges')}</p>
                        <p className="text-sm text-muted-foreground">{t('notificationsAboutScheduleUpdates')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="scheduleChanges"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('incidentReports')}</p>
                        <p className="text-sm text-muted-foreground">{t('notificationsAboutSecurityIncidents')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="incidentReports"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between space-y-0 rounded-md border p-4">
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{t('serviceAccountsActivity')}</p>
                        <p className="text-sm text-muted-foreground">{t('notificationsAboutServiceAccountUsage')}</p>
                      </div>
                    </div>
                    <FormField
                      control={form.control}
                      name="serviceAccounts"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch 
                              checked={field.value} 
                              onCheckedChange={field.onChange} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <div className="flex justify-end">
                <Button type="submit" disabled={saving}>
                  {saving ? t('saving') : t('savePreferences')}
                </Button>
              </div>
            </form>
          </Form>
        </Tabs>
      </CardContent>
    </Card>
  );
};
